import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes } from 'react-icons/fa';
import { containerVariants, cardVariants, fadeUp } from '../utils/variants';

const SchoolInquiryModal = ({ isOpen, onClose }) => {
  const [form, setForm] = useState({
    schoolName: '',
    contactName: '',
    phone: '',
    city: '',
    address: '',
    strength: '',
    products: [],
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');

  const WHATSAPP_NUMBER = '919254147455';

  /* 🔒 Lock scroll when modal opens */
  useEffect(() => {
    if (isOpen) {
      const scrollY = window.scrollY;
      document.body.dataset.scrollY = scrollY;
      document.body.style.overflow = 'hidden';
      window.scrollTo({ top: 0 });
    } else {
      document.body.style.overflow = 'auto';
      const prevScroll = document.body.dataset.scrollY || 0;
      window.scrollTo(0, prevScroll);
    }

    return () => {
      document.body.style.overflow = 'auto';
    };
  }, [isOpen]);

  /* Input change */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  /* Product checkbox change */
  const handleProductChange = (e) => {
    const { value, checked } = e.target;

    setForm((prev) => ({
      ...prev,
      products: checked
        ? [...prev.products, value]
        : prev.products.filter((item) => item !== value),
    }));
  };

  /* Submit */
  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const message = `
*🏫 School Inquiry Details*

*School Name:* ${form.schoolName}
*Contact Person:* ${form.contactName}
*Phone:* ${form.phone}
*City:* ${form.city}
*School Strength:* ${form.strength}
*Products Required:* ${form.products.join(', ') || 'Not selected'}
*Address:* ${form.address}
*Message:* ${form.message}
    `;

    const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(
      message
    )}`;

    try {
      window.open(whatsappUrl, '_blank');
      setSuccess('Message sent successfully!');
      setError('');
      setForm({
        schoolName: '',
        contactName: '',
        phone: '',
        city: '',
        address: '',
        strength: '',
        products: [],
        message: '',
      });
    } catch {
      setError('Failed to send message. Please try again.');
      setSuccess('');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
         <motion.div
  className="
    bg-gradient-to-br from-[#E0F2FE] to-[#FFFFFF]
    rounded-lg shadow-lg
    md:p-6 p-4
    max-w-lg w-full
    relative
    max-h-[95vh]
    overflow-y-auto
  "
  variants={containerVariants}
  initial="hidden"
  animate="visible"
  exit={{ opacity: 0, scale: 0.9 }}
>

            {/* Close Button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 text-[#1E293B] text-2xl hover:text-[#0369A1]"
            >
              <FaTimes />
            </button>

            <h2 className="text-2xl font-semibold text-[#1E293B] mb-2">
              School Information Form
            </h2>

            <p className="text-[#1E293B] mb-3">
              Fill in the details and our team will contact you shortly.
            </p>

            <motion.div
              className="bg-white bg-opacity-70 p-4 md:p-5 rounded-lg shadow-inner"
              variants={cardVariants}
            >
              <form onSubmit={handleSubmit}>
                {/* School Name */}
                <motion.input
                  name="schoolName"
                  value={form.schoolName}
                  onChange={handleChange}
                  placeholder="School Name"
                  className="w-full mb-3 px-3 py-2 rounded-full bg-white focus:outline-none focus:ring-2 focus:ring-[#0284C7]"
                  required
                  variants={fadeUp}
                />

                {/* Contact Person */}
                <motion.input
                  name="contactName"
                  value={form.contactName}
                  onChange={handleChange}
                  placeholder="Contact Person Name"
                  className="w-full mb-3 px-3 py-2 rounded-full bg-white focus:outline-none focus:ring-2 focus:ring-[#0284C7]"
                  required
                  variants={fadeUp}
                />

                {/* Phone */}
                <motion.input
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  placeholder="Phone Number"
                  className="w-full mb-3 px-3 py-2 rounded-full bg-white focus:outline-none focus:ring-2 focus:ring-[#0284C7]"
                  required
                  variants={fadeUp}
                />

                {/* City */}
                <motion.input
                  name="city"
                  value={form.city}
                  onChange={handleChange}
                  placeholder="City"
                  className="w-full mb-3 px-3 py-2 rounded-full bg-white focus:outline-none focus:ring-2 focus:ring-[#0284C7]"
                  required
                  variants={fadeUp}
                />

                {/* Strength */}
                <motion.input
                  name="strength"
                  value={form.strength}
                  onChange={handleChange}
                  placeholder="Total School Strength"
                  className="w-full mb-3 px-3 py-2 rounded-full bg-white focus:outline-none focus:ring-2 focus:ring-[#0284C7]"
                  required
                  variants={fadeUp}
                />

                {/* Products */}
                <div className="mb-4">
                  <p className="text-[#1E293B] font-medium mb-2">
                    Products Required
                  </p>

                  <div className="flex gap-6 justify-center">
                    {['Website', 'Mobile App', 'Both'].map((item) => (
                      <label
                        key={item}
                        className="flex items-center gap-2 text-[#1E293B] cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          value={item}
                          checked={form.products.includes(item)}
                          onChange={handleProductChange}
                          className="w-4 h-4 accent-[#0284C7]"
                        />
                        {item}
                      </label>
                    ))}
                  </div>
                </div>

                {/* Address */}
                <motion.textarea
                  name="address"
                  value={form.address}
                  onChange={handleChange}
                  placeholder="School Address"
                  rows="2"
                  className="w-full mb-3 px-3 py-2 rounded-2xl bg-white focus:outline-none focus:ring-2 focus:ring-[#0284C7]"
                  required
                  variants={fadeUp}
                />

                {/* Message */}
                <motion.textarea
                  name="message"
                  value={form.message}
                  onChange={handleChange}
                  placeholder="Additional Message"
                  rows="2"
                  className="w-full mb-3 px-3 py-2 rounded-2xl bg-white focus:outline-none focus:ring-2 focus:ring-[#0284C7]"
                  variants={fadeUp}
                />

                {success && (
                  <p className="text-green-600 text-center mb-2">{success}</p>
                )}
                {error && (
                  <p className="text-red-600 text-center mb-2">{error}</p>
                )}

                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-[#0284C7] text-white py-3 rounded-full hover:bg-[#0369A1] font-semibold transition"
                  variants={fadeUp}
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </motion.button>
              </form>
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SchoolInquiryModal;
